import { useMe } from "../lib/session";
import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError, onUnlockChange, setUnlockHandler, unlockChanged, type CodeChallenge } from "../lib/api";
import { ErrorText } from "./ui";

// Changing company settings needs a code we email to the person. One code
// unlocks this device for a while (the API decides how long). The dialog
// opens by itself when a change needs it, and the change goes through once
// the code is right.

interface UnlockStatus { unlocked_until: string | null; minutes: number }

export function useUnlock() {
  const qc = useQueryClient();
  const support = !!useMe().data?.support;
  const q = useQuery({ queryKey: ["unlock"], queryFn: () => api<UnlockStatus>("/v1/auth/unlock"), staleTime: 30_000, enabled: !support });
  useEffect(() => onUnlockChange(() => qc.invalidateQueries({ queryKey: ["unlock"] })), [qc]);
  const until = q.data?.unlocked_until ? new Date(q.data.unlocked_until) : null;
  const [, tick] = useState(0);
  useEffect(() => {
    if (!until) return;
    const t = setInterval(() => tick((n) => n + 1), 20_000);
    return () => clearInterval(t);
  }, [until?.getTime()]);
  const open = !!until && until.getTime() > Date.now();
  return { open, until, minutes: q.data?.minutes ?? 15 };
}

/** UnlockHost listens for settings changes that need a code. Mount once. */
export function UnlockHost() {
  const [pending, setPending] = useState<((ok: boolean) => void) | null>(null);
  useEffect(() => {
    setUnlockHandler(() => new Promise<boolean>((resolve) => setPending(() => resolve)));
    return () => setUnlockHandler(null);
  }, []);
  if (!pending) return null;
  return <UnlockDialog onDone={(ok) => { pending(ok); setPending(null); }} />;
}

/** UnlockButton lets people unlock (or lock) settings before they start. */
export function UnlockBar() {
  const { open, until, minutes } = useUnlock();
  const [asking, setAsking] = useState(false);
  const support = !!useMe().data?.support;
  if (support) return null; // SVENEX support never changes these settings
  const left = until ? Math.max(1, Math.round((until.getTime() - Date.now()) / 60_000)) : 0;
  return (
    <div className={`unlock-bar${open ? " open" : ""}`}>
      <span className="unlock-ico" aria-hidden>
        <svg viewBox="0 0 24 24"><path d={open ? "M7 11V8a5 5 0 0 1 9.6-1.9M5 11h14v9H5z" : "M7 11V8a5 5 0 0 1 10 0v3M5 11h14v9H5z"} /></svg>
      </span>
      <span className="unlock-text">
        {open ? <>Company details are unlocked on this device for {left} more minute{left === 1 ? "" : "s"}.</> : <>Changing company details needs a code we email you. One code unlocks them for {minutes} minutes on this device.</>}
      </span>
      {open ? (
        <button className="secondary small" onClick={async () => { await api("/v1/auth/unlock", { method: "DELETE" }); unlockChanged(); }}>Lock now</button>
      ) : (
        <button className="secondary small" onClick={() => setAsking(true)}>Unlock</button>
      )}
      {asking && <UnlockDialog onDone={() => setAsking(false)} />}
    </div>
  );
}

function UnlockDialog({ onDone }: { onDone: (ok: boolean) => void }) {
  const [ch, setCh] = useState<CodeChallenge | null>(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [resent, setResent] = useState(false);
  const started = useRef(false);
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    api<CodeChallenge>("/v1/auth/unlock/start", { method: "POST", json: {} }).then(setCh, setError);
  }, []);
  useEffect(() => { if (ch) input.current?.focus(); }, [ch]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onDone(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onDone]);
  const submit = async () => {
    if (!ch) return;
    setBusy(true); setError(null);
    try {
      await api("/v1/auth/unlock", { method: "POST", json: { challenge_token: ch.challenge_token, code } });
      unlockChanged();
      onDone(true);
    } catch (e) {
      setError(e);
      if (e instanceof ApiError && e.code === "code_attempts_exceeded") setCh(null);
    } finally { setBusy(false); }
  };
  return (
    <div className="modal-scrim" onMouseDown={(e) => { if (e.target === e.currentTarget) onDone(false); }}>
      <div className="modal" role="dialog" aria-modal="true" aria-labelledby="unlock-title">
        <h2 id="unlock-title">Confirm it's you</h2>
        {!ch && !error && <p className="muted">Sending a code to your email…</p>}
        {ch && <p className="muted">We emailed a 6-digit code to <b>{ch.email_hint}</b>. Enter it to change your company details. It unlocks them on this device for 15 minutes.</p>}
        <form className="form" onSubmit={(e) => { e.preventDefault(); submit(); }}>
          {ch && (
            <input ref={input} className="code-input" aria-label="Code" inputMode="numeric" autoComplete="one-time-code" maxLength={6} placeholder="000000"
              value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} />
          )}
          <ErrorText error={error} />
          <div className="inline">
            {ch && <button type="submit" disabled={busy || code.length !== 6}>{busy ? "Checking…" : "Unlock"}</button>}
            {ch && (
              <button type="button" className="secondary" disabled={resent} onClick={async () => {
                setError(null);
                try { await api("/v1/auth/account-code/resend", { method: "POST", json: { challenge_token: ch.challenge_token, purpose: "unlock" } }); setResent(true); } catch (e) { setError(e); }
              }}>{resent ? "New code sent" : "Send a new code"}</button>
            )}
            {!ch && !!error && <button type="button" className="secondary" onClick={() => { setError(null); api<CodeChallenge>("/v1/auth/unlock/start", { method: "POST", json: {} }).then(setCh, setError); }}>Try again</button>}
            <span className="spacer" />
            <button type="button" className="link" onClick={() => onDone(false)}>Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
}
